import { useState } from "preact/hooks"
import useSWR from "swr"
import { Link } from "wouter-preact"
import { formatDistanceToNow, parseISO } from "date-fns"
import { RefreshCw } from "lucide-react"
import { capitalCase } from "change-case"
import {
  AccountingIntegrationStats,
  IntegrationWithJobInfo,
  getAccountingIntegrationStats,
  triggerSync,
} from "@/api/integration"
import { allIntegrations } from "@/setup/connect/integrations"
import { XeroReconnectNotice, xeroNeedsReauth } from "@/accounting/xero_reconnect"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { classes } from "@/utils"

function isStaleBusinessDate(date: Date, integrationName?: string): boolean {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  if (integrationName === "cands" || integrationName === "svharbor") {
    return diffHours > 168
  }

  const dayOfWeek = date.getDay()
  if (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) {
    return diffHours > 72
  }
  return diffHours > 24
}

export function formatAccountingStatsParts(stats: AccountingIntegrationStats): string[] {
  const parts: string[] = []
  if (stats.gl_accounts > 0) {
    parts.push(`${stats.gl_accounts.toLocaleString()} GL accounts`)
  }
  if (stats.invoices > 0) {
    parts.push(`${stats.invoices.toLocaleString()} invoices`)
  }
  if (stats.checks > 0) {
    parts.push(`${stats.checks.toLocaleString()} payments`)
  }
  return parts
}

interface AccountingSyncBannerProps {
  integration: IntegrationWithJobInfo
  onSynced: () => void
}

export function AccountingSyncBanner({ integration, onSynced }: AccountingSyncBannerProps) {
  const [syncing, set_syncing] = useState(false)

  const config = allIntegrations.find(i => i.id === integration.name)
  const displayName = config?.name ?? capitalCase(integration.name)
  const settingsPath = config?.settingsPath ?? `/settings/config/${integration.name}`

  const { data: stats_result, mutate: mutate_stats } = useSWR<ReturnType<typeof getAccountingIntegrationStats>>(
    `accounting-stats-${integration.id}`,
    () => getAccountingIntegrationStats(integration.id)
  )
  const stats: AccountingIntegrationStats | null =
    stats_result && (stats_result as any).ok ? (stats_result as any).value.data : null

  const last_sync_at = integration.last_sync_job?.created_at ?? null
  const has_error = !!integration.last_sync_job?.error
  const sync_date = last_sync_at ? parseISO(last_sync_at) : null
  const is_stale = sync_date ? isStaleBusinessDate(sync_date, integration.name) : false

  const last_sync_label = last_sync_at
    ? `Last sync: ${formatDistanceToNow(parseISO(last_sync_at), { addSuffix: true })}${is_stale ? " (stale)" : ""}`
    : "Not yet synced"

  const stats_parts = stats ? formatAccountingStatsParts(stats) : []

  async function handle_sync_now() {
    set_syncing(true)
    const res = await triggerSync(integration.id)
    if (res.ok) {
      toast({
        title: "Sync started",
        description: `${displayName} sync job queued — check back in a moment.`,
      })
      void mutate_stats()
      onSynced()
    } else {
      toast({ title: "Sync failed", description: res.error.message, variant: "destructive" })
    }
    set_syncing(false)
  }

  // Surface a dead Xero connection here rather than letting the user discover it mid-apply. Nothing
  // below (sync, stats) can work until it's re-authorized.
  if (xeroNeedsReauth(integration)) {
    return <XeroReconnectNotice />
  }

  return (
    <div class="flex flex-wrap items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
      {config?.logo && (
        <img src={config.logo} alt={displayName} class="h-8 w-8 object-contain shrink-0" />
      )}
      <div class="flex flex-col min-w-0">
        <span class="text-emerald-700 font-semibold">{displayName}</span>
        <span class="text-emerald-600 truncate">{integration.username}</span>
      </div>
      {stats_parts.length > 0 && (
        <>
          <span class="text-emerald-400 hidden sm:inline">·</span>
          <span class="text-emerald-700 font-medium">{stats_parts.join(" · ")}</span>
        </>
      )}
      <span class="text-emerald-400 hidden sm:inline">·</span>
      <span
        class={classes(
          has_error ? "text-red-600 font-medium" : is_stale ? "text-red-600" : "text-emerald-600"
        )}>
        {has_error ? "Sync error — see connection details" : last_sync_label}
      </span>
      <div class="ml-auto flex items-center gap-2">
        <a
          href="#accounting-synced-data"
          class="text-emerald-700 hover:text-emerald-900 text-sm underline-offset-2 hover:underline">
          Inspect synced data
        </a>
        <Link href={settingsPath} class="text-emerald-700 hover:text-emerald-900 text-sm underline-offset-2 hover:underline">
          View details
        </Link>
        <Button
          size="sm"
          variant="outline"
          class="border-emerald-300 text-emerald-700 hover:bg-emerald-100 gap-1.5"
          disabled={syncing}
          onClick={() => void handle_sync_now()}>
          <RefreshCw className={classes("h-3.5 w-3.5", syncing ? "animate-spin" : "")} />
          {syncing ? "Syncing…" : "Sync Now"}
        </Button>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("formatAccountingStatsParts", () => {
    it("includes only non-zero counts", () => {
      expect(
        formatAccountingStatsParts({ gl_accounts: 5, invoices: 0, checks: 12 })
      ).toEqual(["5 GL accounts", "12 payments"])
    })

    it("returns empty array when all counts are zero", () => {
      expect(formatAccountingStatsParts({ gl_accounts: 0, invoices: 0, checks: 0 })).toEqual([])
    })
  })
}
